import { CircularProgress, ThemeProvider, StyledEngineProvider } from '@mui/material';
import { ThemeProvider as StylesThemeProvider } from '@mui/styles';
import Button from '@mui/material/Button';
import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import CountdownTimer from './components/Helpers/Time/CountdownTimer';
import { getTokenRemainingTime, redirectToLogin, refreshToken } from './auth';
import Theme from './components/Theme/Theme';
import * as Sentry from "@sentry/react";
import "./SessionTimeout.scss";

export default function SessionTimeout(props) {
  const [loading, setLoading] = useState(false);

  const [modalActive, setModalActive] = useState(false);
  const modalActiveRef = useRef(modalActive);
  useEffect(() => {
    modalActiveRef.current = modalActive;
  }, [modalActive]);

  const [remainingMinutes, setRemainingMinutes] = useState(5);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  const notifyEventRef = useRef(null);

  const clearTimer = () => {
    if (notifyEventRef.current) {
      clearTimeout(notifyEventRef.current);
    }
  }

  const timeOut = () => {
    Sentry.captureMessage("Session timeout - redirecting to login", {
      level: "info",
    });
    redirectToLogin();
  };

  const logOut = () => {
    Sentry.captureMessage("Manual logout from modal - redirecting to login", {
      level: "info",
    });
    redirectToLogin();
  };

  const checkSession = () => {
    const remainingTime = getTokenRemainingTime();
    if (remainingTime == 0) {
      timeOut();
    } else if (remainingTime < (5 * 60)) {
      setRemainingMinutes(Math.floor(remainingTime / 60));
      setRemainingSeconds(remainingTime % 60);
      setModalActive(true);
    } else {
      setModalActive(false);
      clearTimer();
      const notifyTimeMS = 1000 * (remainingTime - 5 * 60); // Show modal 5 min prior to timeout
      notifyEventRef.current = setTimeout(() => {
        setModalActive(true);
        setRemainingMinutes(Math.floor(getTokenRemainingTime() / 60));
        setRemainingSeconds(getTokenRemainingTime() % 60);
      }, notifyTimeMS);
    }
  }

  const extendTimeout = async () => {
    if (getTokenRemainingTime() == 0) {
      logOut();
    }

    setLoading(true);

    try {
      await refreshToken();
      setModalActive(false);
      setLoading(false);
      checkSession();
    }
    catch (err) {
      setModalActive(false);
      console.log(err);
    }
  };

  const focusHandler = () => {
    checkSession();
  }

  const clickHandler = () => {
    const isModalActive = modalActiveRef.current;
    if (!isModalActive) {
      extendTimeout();
    }
  }

  const eventListeners = {
    click: clickHandler,
    keydown: clickHandler,
    focus: focusHandler,
  };

  const addEventListeners = () => {
    for (let e in eventListeners) {
      window.addEventListener(e, eventListeners[e]);
    }
  }

  useEffect(() => {
    addEventListeners();
    extendTimeout();
  }, []);

  const theme = Theme.get();

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <StylesThemeProvider theme={theme}>
          <React.Fragment>
            <Modal className="insured-info-content" show={modalActive} animation={false} backdrop="static" onHide={() => setModalActive(false)} centered>
              <Modal.Body className="d-flex align-items-center justify-content-center p-3 flex-column">
                <p className="pb-4 pt-2 text-center insured-info">Your session is about to expire. If you do not extend your session within 5 minutes, any unsaved progress will be lost.</p>
                <CountdownTimer initialMinutes={remainingMinutes} initialSeconds={remainingSeconds} onTimerComplete={() => logOut()} />
                {
                  loading ? <CircularProgress className="circular-progress" /> :
                    <div className="button-container">
                      <Button className="text-center px-4 w-25"
                        variant="contained"
                        size="medium"
                        color="primary"
                        onClick={() => extendTimeout()}
                      >
                        Extend
                      </Button>
                      <Button className="text-center px-4 w-25"
                        variant="contained"
                        size="medium"
                        color="primary"
                        onClick={() => logOut()}
                      >
                        Log Out
                      </Button>
                    </div>
                }
              </Modal.Body>
            </Modal>
          </React.Fragment>
        </StylesThemeProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

SessionTimeout.propTypes = {};
